import { Box, Tooltip, Typography } from "@mui/material";
import type { FC } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import type { KpiAvgTimeDistributionBucket } from "../types";

interface KpiTimeDistributionChartProps {
  title: string;
  data: KpiAvgTimeDistributionBucket[];
}

function TooltipBody({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as KpiAvgTimeDistributionBucket;

  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 1,
        maxWidth: 220,
      }}
    >
      <Tooltip title={row.bucketLabel} placement="top" enterDelay={400}>
        <Typography
          variant="caption"
          display="block"
          sx={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.bucketLabel}
        </Typography>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" component="span">
        {row.count} משימות
      </Typography>
    </Box>
  );
}

export const KpiTimeDistributionChart: FC<KpiTimeDistributionChartProps> = ({
  title,
  data,
}) => {
  const sorted = [...data].sort((a, b) => a.bucketKey - b.bucketKey);
  const hasData = sorted.some((d) => d.count > 0);
  const denseAxis = sorted.length > 7;
  const maxCount = sorted.length ? Math.max(...sorted.map((r) => r.count)) : 0;
  const yAxisWidth = maxCount >= 1000 ? 72 : maxCount >= 100 ? 64 : 56;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Tooltip title={title} placement="top" enterDelay={400}>
        <Box sx={{ width: "100%", mb: 1 }}>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "text.primary",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
        </Box>
      </Tooltip>

      {!hasData ? (
        <Box
          sx={{
            py: 1.5,
            px: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            אין נתונים
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: { xs: 360, md: 500 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={sorted}
              margin={{ top: 16, right: 36, left: 16, bottom: 16 }}
            >
              <CartesianGrid strokeDasharray="4 4" />
              <XAxis
                dataKey="bucketLabel"
                tick={{ fontSize: 12 }}
                interval={0}
                angle={denseAxis ? -20 : 0}
                textAnchor={denseAxis ? "end" : "middle"}
                height={denseAxis ? 72 : 40}
              />
              <YAxis
                allowDecimals={false}
                width={yAxisWidth}
                tickMargin={6}
                tick={{ fontSize: 12 }}
                label={{
                  value: "כמות",
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: 12 },
                }}
              />
              <RechartsTooltip
                content={<TooltipBody />}
                wrapperStyle={{ outline: "none" }}
              />
              <Line
                type="linear"
                dataKey="count"
                stroke="#05204c"
                strokeWidth={3}
                dot={{ r: 5, fill: "#05204c" }}
                activeDot={{ r: 7 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};
